import shortid from 'shortid'

import CookieService from 'services/cookie.service'

import type { CardType } from 'types/deck'
import type { RecommendationCardType } from 'types/recommendations'

import { defaultCard } from 'types/deck'
import { getCardDefaultCategoryFromCard } from './CategoryGenerator'
import { DeckCustomCard } from 'services/apiTypes/deck.types'

import { convertCustomCardsToCardType } from 'hooks/customCard'

const pushToList = (colorString: string, list: Array<any>) => {
  colorString
    .trim()
    .replace(/{/g, '')
    .split('}')
    .forEach(symbol => {
      if (symbol.length > 0) {
        if (symbol.search('/') !== -1) {
          //Maybe split and push array instead
          list.push(symbol.split('/'))
        } else {
          list.push(symbol.replace('∞', 'I'))
        }
      }
    })
}

export const colorStringList = (colorString: string): Array<string> => {
  let results: Array<string> = []
  // console.log(colorString)
  const colorSplit = colorString.replace(/ /g, '').split('//')
  pushToList(colorSplit[0], results)
  if (colorSplit.length > 1) {
    results.push('/')
    pushToList(colorSplit[1], results)
  }
  return results
}

// DEPRICATED - DO NOT USE FOR NEW CODE
// TODO - Refactor this out entirely for old code (eg: Search redux)
export const responseToResults = (response: Array<Record<string, any>>): Array<CardType> => {
  const results: Array<CardType> = []

  response.forEach(card => {
    if (card.layout === 'meld' && card.meldBack === null) return

    results.push(responseToCard(card.card, card))
  })

  return results
}

export const responseToDeckCards = (
  resposne: Array<Record<string, any>>,
  unlinkFromDeck = false,
  overrideUserId?: number,
  overrideShowUploadedImages?: boolean,
  useCardsActualIds?: boolean,
): Array<CardType> => {
  return resposne.reduce((acc: CardType[], deckCard: any) => {
    const card = deckCard.card

    if (deckCard.customCard) {
      // Converts CustomCard => CardType
      const convertedCard = convertCustomCardsToCardType(
        deckCard.customCard,
        true,
        overrideUserId,
        overrideShowUploadedImages,
      )[0]

      // Adds all the meta info for DeckCards (eg: quantity, foil status, etc)
      const cardWithDeckMeta = responseToCard(convertedCard, deckCard, unlinkFromDeck, useCardsActualIds)

      acc.push(cardWithDeckMeta)

      return acc
    }

    if (card.layout === 'meld' && card.meldBack === null) return acc

    const serializedCard = responseToCard(card, deckCard, unlinkFromDeck)

    acc.push(serializedCard)

    return acc
  }, new Array<CardType>())
}

export const responseToCustomCards = (
  response: DeckCustomCard[],
  unlinkFromDeck = false,
  overrideUserId?: number,
  overrideShowUploadedImages?: boolean,
  useCardsActualIds = false,
): CardType[] => {
  return response.map(deckCard => {
    const [card] = convertCustomCardsToCardType(deckCard.card, false, overrideUserId, overrideShowUploadedImages)

    // Response to card is expecting the card ID to be card.id, then movies that to card.cardId, and generates the short ID for card.id (used only in the browser).
    // Bit convoluted, but we're trying to match existing data layouts to reduce the amount of custom code paths required for custom cards
    return responseToCard({ ...card, id: null }, { ...deckCard, card }, unlinkFromDeck, useCardsActualIds)
  })
}

export const responseToCard = (
  card: Record<string, any>,
  extraCardMetaData: Record<string, any> = {},
  unlinkFromDeck = false,
  useCardsActualIds = false,
): CardType | RecommendationCardType => {
  let colorLabel = { name: '', color: '#656565' }
  const label = extraCardMetaData.label

  if (label) {
    const split = label.split(',')

    if (split.length === 2) colorLabel = { name: split[0], color: split[1] }
  }

  card = {
    ...card.oracleCard,
    ...card,
  }

  const OptionsOrder: Record<string, number> = { 'Normal': 1, 'Foil': 2, 'Etched': 3 }
  const orderedOptions = (card.options || []).sort((a: string, b: string) =>
    (OptionsOrder[a] || 1000) < (OptionsOrder[b] || 1000) ? -1 : 1,
  )

  const deckRelationId = extraCardMetaData.id && !unlinkFromDeck ? extraCardMetaData.id.toString() : ''

  const serializedCard = {
    ...defaultCard,

    // Deck Card Properties
    qty: card.qty || extraCardMetaData.quantity || 0, // card.qty is only used by the ImportCard page
    modifier: extraCardMetaData.modifier || orderedOptions[0],
    typeCategory: 'Default',
    categories: [],
    colorLabel: colorLabel,
    customCmc: typeof extraCardMetaData.customCmc === 'number' ? extraCardMetaData.customCmc : null,
    companion: extraCardMetaData.companion || false,
    flippedDefault: extraCardMetaData.flippedDefault || false,

    // Collection Properites
    owned: card.owned || 0,

    pinnedStatus: card.pinnedStatus || 0,

    // Card Properties
    prices: {
      ck: card.prices !== null && card.prices.ck ? Number(card.prices.ck) : 0.0,
      ckFoil: card.prices !== null && card.prices.ckfoil ? Number(card.prices.ckfoil) : 0.0,
      tcg: card.prices !== null && card.prices.tcg ? Number(card.prices.tcg) : 0.0,
      tcgFoil: card.prices !== null && card.prices.tcgfoil ? Number(card.prices.tcgfoil) : 0.0,
      mtgo: card.prices !== null && card.prices.mtgo ? Number(card.prices.mtgo) : 0.0,
      mtgoFoil: card.prices !== null && card.prices.mtgofoil ? Number(card.prices.mtgofoil) : 0.0,
      cm: card.prices !== null && card.prices.cm ? Number(card.prices.cm) : 0.0,
      cmFoil: card.prices !== null && card.prices.cmfoil ? Number(card.prices.cmfoil) : 0.0,
      scg: card.prices !== null && card.prices.scg ? Number(card.prices.scg) : 0.0,
      scgFoil: card.prices !== null && card.prices.scgfoil ? Number(card.prices.scgfoil) : 0.0,
      mp: card.prices !== null && card.prices.mp ? Number(card.prices.mp) : 0.0,
      mpFoil: card.prices !== null && card.prices.mpfoil ? Number(card.prices.mpfoil) : 0.0,
      tcgland: card.prices !== null && card.prices.tcgLand ? Number(card.prices.tcgLand) : 0.0,
      tcglandFoil: card.prices !== null && card.prices.tcgLandFoil ? Number(card.prices.tcgLandFoil) : 0.0,
    },
    name: card.name ? card.name : '',
    displayName: card.displayName || null,
    cmc: card.cmc ? card.cmc : 0,
    rarity: card.rarity ? card.rarity : '',

    // Requires formatting because we creat card.castingCost correctly for custom cards prior to serializing them as normal cards
    castingCost: Array.isArray(card.castingCost)
      ? card.castingCost
      : card.manaCost
        ? colorStringList(card.manaCost)
        : [],

    colorIdentity: card.colorIdentity ? card.colorIdentity : [],
    colors: card.colors ? card.colors : [],
    manaProduction: card.manaProduction ? card.manaProduction : {},
    text: card.text ? card.text : '',
    flavor: card.flavor ? card.flavor : '',
    setCode: card.setCode || (card.edition ? card.edition.editioncode : ''),
    mtgoCode: card.edition && card.edition.mtgoCode ? card.edition.mtgoCode : '',
    releasedAt: card.releasedAt || null,
    setDate: (card.edition && card.edition.editiondate) || '',
    setType: (card.edition && card.edition.editiontype) || '',
    set: card.edition ? card.edition.editionname : '',
    uid: card.uid ? card.uid : '',
    id: useCardsActualIds ? (card.customCard ? `custom-${card.customCard.id}` : card.id) : shortid.generate(),
    deckRelationId: deckRelationId,
    cardId: card.id?.toString() || null,
    oracleCardId: card.oracleCard?.id || 0,
    multiverseid: card.multiverseid ? card.multiverseid : 0,
    imgurl: card.imgurl ? card.imgurl : '',
    artist: card.artist ? card.artist : '',
    superTypes: card.superTypes ? card.superTypes : [],
    subTypes: card.subTypes ? card.subTypes : [],
    types: card.types ? card.types : [],
    keywords: card.keywords ? card.keywords : [],
    power: card.power ? card.power : '',
    toughness: card.toughness ? card.toughness : '',
    layout: card.layout ? card.layout : 'normal',
    loyalty: card.loyalty ? card.loyalty : '',
    canlanderPoints: card.canlanderPoints ? card.canlanderPoints : null,
    isPDHCommander: card.isPDHCommander ? !!card.isPDHCommander : false,
    names: card.names ? card.names : [],
    lang: card.lang || 'en',
    contentWarning: card.contentWarning || false,

    // Bracket shit
    gameChanger: card.gameChanger || false,
    extraTurns: card.extraTurns || false,
    tutor: card.tutor || false,
    massLandDenial: card.massLandDenial || false,

    atomicCombos: card.atomicCombos || [],
    potentialCombos: card.potentialCombos || [],

    options: orderedOptions,
    legalities: card.legalities ? card.legalities : {},
    front:
      card.front ||
      (card.faces && card.faces.length
        ? { ...card.faces[0], castingCost: colorStringList(card.faces[0].manaCost) }
        : null),
    back:
      card.back ||
      (card.faces && card.faces.length
        ? { ...card.faces[1], castingCost: colorStringList(card.faces[1].manaCost) }
        : null),
    ids: {
      ckNormalId: card.ckNormalId ? card.ckNormalId : 0,
      ckFoilId: card.ckFoilId ? card.ckFoilId : 0,
      mtgoNormalId: card.mtgoNormalId ? card.mtgoNormalId : 0,
      mtgoFoilId: card.mtgoFoilId ? card.mtgoFoilId : 0,
      tcgId: card.tcgProductId ? card.tcgProductId : 0,
      cmEd: card.cmEd ? card.cmEd : '',
      scg: card.scgSku ? card.scgSku : '',
      scgFoil: card.scgFoilSku ? card.scgFoilSku : '',
    },
    collectorNumber: card.collectorNumber ? card.collectorNumber : '',
    salt: card.salt,
    edhrecRank: card.edhrecRank ? card.edhrecRank : null,

    // Recommendation Specific
    percent: card.percent || null,
    tokens: card.tokens || [],
    createdAt: extraCardMetaData.createdAt || null,
    updatedAt: extraCardMetaData.updatedAt || null,
    deletedAt: extraCardMetaData.deletedAt || null,

    defaultCategory: card.oracleCard?.defaultCategory || null,
    globalCategories: card.globalCategories || [],
    scryfallImageHash: card.scryfallImageHash || null,

    customCard: card.customCard
      ? {
          id: card.customCard?.id || null,
          ownerId: card.customCard?.owner || card.customCard?.ownerId || null,
          frontImageUrl: card.customCard?.frontImageUrl || null,
          backImageUrl: card.customCard?.backImageUrl || null,
        }
      : null,
  }

  const { noUseDefaultCategories = false } = CookieService.get('settings') || {}

  const typeCategory = getCardDefaultCategoryFromCard(serializedCard)
  const defaultCategory = !noUseDefaultCategories ? serializedCard.defaultCategory || typeCategory : typeCategory

  let categories = []

  const hasCategories = extraCardMetaData.categories && extraCardMetaData.categories.length
  const hasGlobalCategories = card.globalCategories && card.globalCategories.length

  if (hasCategories) categories = extraCardMetaData.categories
  else if (extraCardMetaData.removedCategories && !Array.isArray(extraCardMetaData.removedCategories))
    categories = extraCardMetaData.removedCategories.split(',')
  else if (hasGlobalCategories) categories = [...card.globalCategories]
  else categories = [defaultCategory]

  return {
    ...serializedCard,
    typeCategory,
    categories,
  }
}
